import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import QuoteForm from "../components/QuoteForm";

const HERO = "https://images.pexels.com/photos/7195559/pexels-photo-7195559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Furniture() {
  const { t } = useLang();

  return (
    <div data-testid="furniture-page">
      {/* Hero */}
      <section className="relative bg-[#0B1B29] text-white overflow-hidden">
        <img
          src={HERO}
          alt="Modern dining showroom"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative pgb-container py-24 md:py-32 max-w-4xl">
          <span className="eyebrow text-[#93C5FD]">{t.furniturePage.eyebrow}</span>
          <h1 className="mt-5 text-4xl md:text-6xl font-black tracking-tighter leading-[1.02]">
            {t.furniturePage.title}
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl leading-relaxed">
            {t.furniturePage.subtitle}
          </p>
          <div className="mt-8 inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full" />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-white">
              {t.common.readyBadge}
            </span>
          </div>
          <div className="mt-8 inline-flex items-center gap-3 border border-white/20 px-5 py-3">
            <span className="text-3xl font-black text-[#93C5FD]" style={{ fontFamily: "Chivo" }}>20+</span>
            <span className="text-sm text-white/80 tracking-wide">{t.furniturePage.experienceBadge}</span>
          </div>
          <div className="mt-10">
            <Link to="/quote" className="btn-accent" data-testid="furniture-hero-cta">
              {t.common.requestQuote} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features split */}
      <section className="py-24 md:py-32 bg-white">
        <div className="pgb-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t.furniturePage.featuresTitle}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              Foshan · Shunde · Guangzhou
            </h2>
            <p className="mt-5 text-[#536878] leading-relaxed max-w-md">
              {t.furniturePage.subtitle}
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {t.furniturePage.features.map((f, idx) => (
              <div
                key={f.title}
                className="bg-white p-7 hover:bg-[#F5F5F7] transition-colors"
                data-testid={`furniture-feature-${idx}`}
              >
                <span className="font-mono text-xs tracking-[0.18em] text-[#1D4ED8]">
                  / {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 text-lg font-bold tracking-tight text-[#0B1B29]">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-[#536878] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories bento */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="pgb-container">
          <div className="mb-12">
            <span className="eyebrow">{t.furniturePage.categoriesTitle}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              Tables · Chairs · Custom
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {t.furniturePage.categories.map((c, idx) => (
              <div
                key={c}
                className="bg-white border border-[#E2E8F0] px-6 py-10 md:py-14 flex items-center justify-between group hover:bg-[#0B1B29] hover:text-white transition-colors cursor-default"
                data-testid={`furniture-cat-${idx}`}
              >
                <span className="text-xl md:text-2xl font-black tracking-tight">
                  {c}
                </span>
                <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section className="py-24 md:py-32 bg-white">
        <div className="pgb-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t.common.requestQuote}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              {t.quotePage.title}
            </h2>
            <p className="mt-5 text-[#536878] leading-relaxed max-w-md">
              {t.quotePage.subtitle}
            </p>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm defaultProduct="furniture" />
          </div>
        </div>
      </section>
    </div>
  );
}
